<template>
  <div class="width100p d-flex flex-row toolBkg pa-2 align-start">
    <div class="d-flex flex-column flex-sm-row flex-wrap">
      <div class="d-flex flex-column flex-md-row flex-wrap">
        <div
          class="mr-2 d-flex flex-row flex-md-column align-center align-md-start"
        >
          <div class="overline mr-2 mr-md-0 labelWidth">Vertex</div>
          <div class="tinyText">{{ parseInt(vertexDist * 1000) }}&rarr;0mm</div>
        </div>
        <div class="mr-6 d-flex flex-column flex-sm-row flex-wrap">
          <div class="" v-for="eye in ['od', 'os']" :key="'mr_' + eye">
            <div
              class="mr-2 mx-2"
              :class="$vuetify.breakpoint.smAndUp ? 'inputDiv' : 'width100p'"
            >
              <v-text-field
                v-model="mrInput[eye]"
                dense
                rounded
                :hide-details="true"
                :background-color="bkgColorClass[eye]"
                class="inputClass"
                clearable
                :placeholder="`${eye.toUpperCase()} Rx`"
                @change="rxChanged(eye)"
                @click.clear="clearRxAnswer(eye)"
              >
                <template v-slot:append>
                  <v-btn
                    x-small
                    icon
                    class="mt-1"
                    :disabled="!mrInput[eye]"
                    @click="toggleCyl(eye)"
                    tabindex="-1"
                    ><v-icon small>mdi-plus-minus-variant</v-icon></v-btn
                  >
                </template>
              </v-text-field>
            </div>
            <div class="answerDiv pl-4">
              <div v-if="rxError[eye]" class="caption red--text">
                Invalid Rx
              </div>
              <div
                v-else-if="vertexStrings[eye]"
                :class="textColorClass[eye]"
                class="ml-2 pl-3 d-flex flex-row align-center"
              >
                {{ vertexStrings[eye] }}
                <!-- <v-icon x-small class="pl-2" color="primary"
              >mdi-open-in-new</v-icon
            > -->
                <rx-information-popup
                  :rxObj="mr[eye]"
                  class="ml-2"
                ></rx-information-popup>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- keratometry stuff -->
      <div class="d-flex flex-column flex-md-row flex-wrap">
        <div class="mr-2">
          <div class="overline labelWidth">Keratometry</div>
        </div>
        <div class="mr-6 d-flex flex-column flex-sm-row flex-wrap">
          <div class="" v-for="eye in ['od', 'os']" :key="'mr_' + eye">
            <div
              class="mr-2 mx-2"
              :class="$vuetify.breakpoint.smAndUp ? 'inputDiv' : 'width100p'"
            >
              <v-text-field
                v-model="kInput[eye]"
                dense
                rounded
                :hide-details="true"
                :background-color="bkgColorClass[eye]"
                class="inputClass"
                clearable
                :placeholder="`${eye.toUpperCase()} Curvature`"
                @change="kChanged(eye)"
                @click.clear="clearKAnswer(eye)"
              >
              </v-text-field>
            </div>
            <div class="answerDiv pl-4">
              <div v-if="rxError[eye]" class="caption red--text">
                Invalid Curvature
              </div>
              <div
                v-else-if="kStrings[eye]"
                :class="textColorClass[eye]"
                class="ml-2 pl-3"
              >
                {{ kStrings[eye] }}
                <!-- <v-icon x-small class="pl-2" color="primary"
              >mdi-open-in-new</v-icon
            > -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <v-spacer></v-spacer>

    <v-btn
      x-small
      text
      color="primary"
      class="mx-2 lowerLeftIcon"
      @click="openDialog"
      ><v-icon x-small color="primary">mdi-cog</v-icon> Settings</v-btn
    >

    <v-btn
      v-if="!hideCloseButton"
      icon
      small
      @click="$store.dispatch('toggleShowQuickCalcs')"
      class="closeIcon"
      color="primary"
    >
      <v-icon color="primary">mdi-close-circle-outline</v-icon>
    </v-btn>

    <!-- THE SETTINGS DIALOG -->
    <v-dialog v-model="dialog" max-width="320">
      <v-card>
        <v-card-title class="text-h5"> Quick Calcs Settings</v-card-title>
        <v-divider></v-divider>
        <div class="pa-4">
          <div class="overline">
            Vertex Distance ({{ vertexDist * 1000 }}mm)
          </div>
          <div>
            <v-btn
              class="mr-2 text-lowercase"
              color="primary"
              depressed
              rounded
              :outlined="vertexDist != vertexOption / 1000"
              x-small
              v-for="vertexOption in vertexOptions"
              :key="vertexOption"
              @click="vertexDist = vertexOption / 1000"
            >
              {{ vertexOption + "mm" }}
            </v-btn>

            <div class="overline mt-4">
              Round refractions (eg
              {{ numToDiopterString(-4.67, 1, round) }} )
            </div>
            <v-btn
              class="mr-2 text-lowercase"
              color="primary"
              depressed
              rounded
              :outlined="round != roundOption"
              x-small
              v-for="roundOption in roundOptions"
              :key="roundOption"
              @click="round = roundOption"
            >
              {{ roundOption }}
            </v-btn>
          </div>
        </div>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn text @click="cancelDialog" small> Cancel </v-btn>

          <v-btn
            color="green "
            small
            depressed
            @click="
              dialog = false;
              vertexRxs();
            "
          >
            Accept
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import RxMethods from "@/assets/js/cls/RxMethods";
import StyleUtilities from "@/assets/js/StyleUtilities";
// import { mapMutations } from "vuex";

export default {
  name: "QuickCalcs",
  components: {
    RxInformationPopup: () =>
      import("@/components/clStuff/RxInformationPopup.vue"),
  },
  mixins: [RxMethods, StyleUtilities],
  props: { hideCloseButton: null },
  data: function () {
    return {
      mrInput: { od: "", os: "" }, // strings
      mr: { od: null, os: null }, // rxObject or null
      vertexStrings: { od: null, os: null },
      rxError: { od: false, os: false },

      kInput: { od: "", os: "" }, // strings
      k: { od: null, os: null },
      kStrings: { od: null, os: null },

      kError: { od: false, os: false },

      calcCylType: { od: -1, os: -1 },
      vertexDist: 0.012,
      round: 0.25,
      dialog: false,
      vertexOptions: [8, 9, 10, 11, 12, 13, 14, 15, 16],
      roundOptions: [0.25, 0.125, 0.01],
      tempVertex: 12,
      tempRound: 0.25,
    };
  },
  methods: {
    // ...mapMutations(["setShowQuickCalcs"]),
    rxChanged(eye) {
      let mrString = this.mrInput[eye];
      if (!mrString) {
        this.clearRxAnswer(eye);
        return;
      }
      //   console.log(mrString);
      //   if (!mrString) this.clearRxAnswer(eye);
      let rxObj = this.parseRxString(mrString);
      let valid = this.isValidRx(rxObj);
      if (!valid) {
        this.rxError[eye] = true;
        // this.mrInput[eye] = null;
      } else {
        this.rxError[eye] = false;
        this.mr[eye] = rxObj;
        this.mrInput[eye] = this.prettyStringOfType(
          rxObj,
          this.calcCylType[eye]
        );
      }
    },
    clearRxAnswer(eye) {
      this.mr[eye] = null;
      this.vertexStrings[eye] = null;
      this.rxError[eye] = false;
    },
    vertexRxs() {
      let rtn = { od: null, os: null };
      let mr = this.mr;
      let vertexDist = this.vertexDist;
      let that = this;

      ["od", "os"].forEach((eye) => {
        let rxObj = mr[eye];
        if (rxObj && that.isValidRx(rxObj)) {
          let vObj = that.diffVertex(rxObj, 0, vertexDist);
          rtn[eye] = this.prettyStringMinus(vObj, this.round);
        }
      });

      this.vertexStrings = rtn;
    },
    toggleCyl(eye) {
      this.calcCylType[eye] = -this.calcCylType[eye];
      this.rxChanged(eye);
    },
    kChanged(eye) {
      console.log(eye);
      let kString = this.kInput[eye];
      if (!kString) {
        this.clearKAnswer(eye);
        return;
      }
      let kObj = this.fullKStringBreaker(kString);
      let valid = this.isValidFullK(kObj);
      if (!valid) {
        this.kError[eye] = true;
      } else {
        this.kError[eye] = false;
        this.k[eye] = kObj;
        this.kInput[eye] = this.prettyFullKstring(kObj);
      }
    },
    convertKs() {
      let rtn = { od: null, os: null };
      let k = this.k;
      let that = this;

      ["od", "os"].forEach((eye) => {
        let kObj = k[eye];
        if (kObj && that.isValidFullK(kObj)) {
          rtn[eye] = this.prettyFullKstringConverted(kObj);
        }
      });

      this.kStrings = rtn;
    },
    clearKAnswer(eye) {
      this.k[eye] = null;
      this.kStrings[eye] = null;
      this.kError[eye] = false;
    },
    openDialog() {
      this.tempVertex = this.vertexDist * 1000;
      this.tempRound = this.round;
      this.dialog = true;
    },
    cancelDialog() {
      this.vertexDist = this.tempVertex / 1000;
      this.round = this.tempRound;
      this.dialog = false;
    },
  },
  computed: {},
  watch: {
    mr: {
      handler: "vertexRxs",
      deep: true,
    },
    vertexDist: {
      handler: "vertexRxs",
    },
    round: {
      handler: "vertexRxs",
    },
    k: {
      handler: "convertKs",
      deep: true,
    },
  },
};
</script>

<style scoped>
.inputClass {
  border: 1px solid gray;
}
.inputDiv {
  width: 240px;
}
.inputDiv >>> .v-text-field {
  padding-top: 0;
  margin-top: 0 !important;
}
/* .inputDiv >>> .v-text-field__slot {
  padding-left: 5px;
} */
.answerDiv {
  min-height: 1.5em;
}
.toolBkg {
  border: 1px solid #959595;
  /* border-left: 1px solid #b6b6b6; */
  /* border-right: 1px solid #b6b6b6; */
  /* background-image: linear-gradient(#e8f9ff, #fff 45%); */
  /* box-shadow: inset 0px 10px 10px -10px #5d6f76f0; */
  /* box-shadow: inset 0px 22px 15px -15px #7777772e; */
  position: relative;
  background-color: #f6fbff;
  border-radius: 3px;
}
.tinyText {
  font-size: 8pt;
}
.labelWidth {
  min-width: 107px;
}
.lowerLeftIcon {
  position: absolute;
  right: 10px;
  bottom: 4px;
}
</style>
